import { useEffect, useState } from "react";
import { Archive, Users as UsersIcon } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { fmtDate, fmtDateDMY, num, rm } from "../lib/format";
import { toast } from "../stores/toast";
import { useAuth } from "../stores/auth";
import { useQueryRecordId } from "../lib/use-query-record";
import { Button, Input, Badge, statusColor, Textarea, useConfirm, PillSegmentSwitcher, EmptyState, Select } from "../components/ui";
import { DataTable, type Column } from "../components/DataTable";
import { ListPageShell } from "../components/shell/ListPageShell";
import { flattenInfinitePages, getNextPageParam } from "../lib/infinite-list";
import { RightSidebarPanel } from "../components/RightSidebarPanel";

interface Role {
  id: string;
  name: string;
  label: string;
}

interface UserRow {
  id: string;
  email: string;
  displayName: string;
  status: string;
  phone: string | null;
  phoneVerifiedAt: string | null;
  roles: Role[];
  walletBalance: number;
  vaultItemCount?: number;
  createdAt: string;
}

function formatMobile(phone: string | null, verified: string | null): string {
  if (!phone) return "—";
  const display = phone.startsWith("60") ? `0${phone.slice(2)}` : phone;
  return verified ? display : `${display} (unverified)`;
}

interface VaultItemRow {
  id: string;
  productId: string;
  name: string;
  image: string | null;
  value: number;
  sourceType: string;
  status: string;
  shipmentOrderId: string | null;
  acquiredAt: string;
}

interface VaultResponse {
  user: { id: string; email: string; displayName: string };
  items: VaultItemRow[];
  summary: { total: number; totalEstValue: number; byStatus: Record<string, number> };
}

type SidebarTab = "account" | "vault" | "flexxi";

interface UsersResponse {
  items: UserRow[];
  total: number;
  page: number;
  pageSize: number;
}

export function Users() {
  const qc = useQueryClient();
  const { can, moduleEnabled } = useAuth();
  const vaultAccess = can("vault.manage") && moduleEnabled("vault");
  const flexxiAccess = can("flexxi.read") && moduleEnabled("instalments");
  const { confirm, dialog } = useConfirm();

  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selected, setSelected] = useState<UserRow | null>(null);
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [creditDelta, setCreditDelta] = useState("");
  const [creditNote, setCreditNote] = useState("");
  const [sidebarTab, setSidebarTab] = useState<SidebarTab>("account");
  const [vaultStatus, setVaultStatus] = useState("");
  const [vaultSource, setVaultSource] = useState("");
  const { recordId, clearRecordId } = useQueryRecordId();
  const [searchParams, setSearchParams] = useSearchParams();

  const PAGE_SIZE = 25;

  const listQuery = useInfiniteQuery({
    queryKey: ["users", search],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      api<UsersResponse>(`/users?search=${encodeURIComponent(search)}&page=${pageParam}&pageSize=${PAGE_SIZE}`),
    getNextPageParam: (lastPage, allPages) =>
      getNextPageParam(
        lastPage.total,
        allPages.reduce((acc, page) => acc + page.items.length, 0),
        PAGE_SIZE,
      ),
  });

  const userRows = flattenInfinitePages(listQuery.data?.pages);
  const listTotal = listQuery.data?.pages[0]?.total ?? 0;
  const hasNextPage = userRows.length < listTotal && (listQuery.hasNextPage ?? false);

  const { data: deepLinkUser } = useQuery({
    queryKey: ["user", recordId],
    queryFn: () => api<{ user: UserRow }>(`/users/${recordId}`),
    enabled: !!recordId,
  });

  useEffect(() => {
    if (!recordId) return;
    const row = userRows.find((user) => user.id === recordId) ?? deepLinkUser?.user;
    if (row) {
      setSelected(row);
      clearRecordId();
    }
  }, [recordId, userRows, deepLinkUser?.user, clearRecordId]);

  const { data: rolesData } = useQuery({
    queryKey: ["rbac-roles"],
    queryFn: () => api<{ roles: Role[] }>("/rbac/roles"),
    enabled: !!selected && can("rbac.manage"),
  });

  useEffect(() => {
    if (selected) {
      setRoleIds(selected.roles.map((r) => r.id));
      setCreditDelta("");
      setCreditNote("");
      const tab = searchParams.get("tab");
      if (tab === "flexxi" && flexxiAccess) setSidebarTab("flexxi");
      else if (tab === "vault" && vaultAccess) setSidebarTab("vault");
      else setSidebarTab("account");
    }
  }, [selected, searchParams, vaultAccess, flexxiAccess]);

  const { data: flexxiData, isLoading: flexxiLoading } = useQuery({
    queryKey: ["user-flexxi", selected?.id],
    queryFn: () =>
      api<{
        profile: { reputationScore: number; activeSlotLimit: number; completedPlans: number } | null;
        plans: { id: string; status: string; totalPayable: string; paidTotal: string; template: { code: string } }[];
      }>(`/admin/flexxi/users/${selected!.id}/plans`),
    enabled: !!selected && sidebarTab === "flexxi" && flexxiAccess,
  });

  const { data: vaultData, isLoading: vaultLoading } = useQuery({
    queryKey: ["user-vault", selected?.id, vaultStatus, vaultSource],
    queryFn: () => {
      const params = new URLSearchParams({ pageSize: "48" });
      if (vaultStatus) params.set("status", vaultStatus);
      if (vaultSource) params.set("sourceType", vaultSource);
      return api<VaultResponse>(`/users/${selected!.id}/vault?${params.toString()}`);
    },
    enabled: !!selected && sidebarTab === "vault" && vaultAccess,
  });

  const rolesMut = useMutation({
    mutationFn: ({ userId, roleIds: ids }: { userId: string; roleIds: string[] }) =>
      api(`/rbac/users/${userId}/roles`, { method: "PUT", body: { roleIds: ids } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["users"] });
      toast("success", "Roles updated");
    },
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "Role update failed"),
  });

  const suspendMut = useMutation({
    mutationFn: ({ userId, suspend }: { userId: string; suspend: boolean }) =>
      api(`/users/${userId}/suspend`, { method: "POST", body: { suspend } }),
    onSuccess: (_, { suspend }) => {
      void qc.invalidateQueries({ queryKey: ["users"] });
      toast("success", suspend ? "User suspended" : "User reactivated");
      setSelected(null);
    },
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "Suspend failed"),
  });

  const creditMut = useMutation({
    mutationFn: ({ userId, delta, note }: { userId: string; delta: number; note: string }) =>
      api(`/users/${userId}/adjust-credit`, { method: "POST", body: { delta, note } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["users"] });
      toast("success", "Credit adjusted");
      setCreditDelta("");
      setCreditNote("");
    },
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "Credit adjustment failed"),
  });

  const columns: Column<UserRow>[] = [
    {
      key: "name",
      header: "User",
      sortable: true,
      sortValue: (r) => r.displayName,
      exportValue: (r) => `${r.displayName} (${r.email})`,
      render: (r) => (
        <div>
          <p className="font-medium">{r.displayName}</p>
          <p className="text-[10px] text-muted-2">{r.email}</p>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      sortValue: (r) => r.status,
      render: (r) => <Badge color={statusColor(r.status)}>{r.status}</Badge>,
    },
    {
      key: "mobile",
      header: "Mobile",
      hideSm: true,
      sortable: true,
      sortValue: (r) => r.phone ?? "",
      exportValue: (r) => formatMobile(r.phone, r.phoneVerifiedAt),
      render: (r) => (
        <span className={`text-xs ${r.phoneVerifiedAt ? "text-muted" : "text-muted-2"}`}>
          {formatMobile(r.phone, r.phoneVerifiedAt)}
        </span>
      ),
    },
    {
      key: "roles",
      header: "Roles",
      hideSm: true,
      sortable: true,
      sortValue: (r) => r.roles.map((role) => role.label).join(", "),
      exportValue: (r) => r.roles.map((role) => role.label).join(", "),
      render: (r) => (
        <span className="text-xs text-muted">
          {r.roles.length ? r.roles.map((role) => role.label).join(", ") : "—"}
        </span>
      ),
    },
    {
      key: "balance",
      header: "Credits",
      className: "text-right",
      sortable: true,
      sortValue: (r) => r.walletBalance,
      exportValue: (r) => r.walletBalance,
      render: (r) => <span className="font-display tabular-nums text-brand-pink">{num(r.walletBalance)}</span>,
    },
    ...(vaultAccess
      ? [
          {
            key: "vault",
            header: "Vault",
            className: "text-right",
            sortable: true,
            sortValue: (r: UserRow) => r.vaultItemCount ?? 0,
            exportValue: (r: UserRow) => r.vaultItemCount ?? 0,
            render: (r: UserRow) => (
              <span className="tabular-nums text-xs text-muted">{r.vaultItemCount ?? 0}</span>
            ),
          } satisfies Column<UserRow>,
        ]
      : []),
    {
      key: "joined",
      header: "Joined",
      hideSm: true,
      sortable: true,
      sortValue: (r) => r.createdAt,
      exportValue: (r) => fmtDate(r.createdAt),
      render: (r) => <span className="text-xs text-muted">{fmtDate(r.createdAt)}</span>,
    },
  ];

  function toggleRole(roleId: string) {
    setRoleIds((ids) => (ids.includes(roleId) ? ids.filter((id) => id !== roleId) : [...ids, roleId]));
  }

  return (
    <ListPageShell>
      {dialog}

      <DataTable
        columns={columns}
        rows={userRows}
        rowKey={(r) => r.id}
        loading={listQuery.isLoading}
        exportFilename="users"
        infiniteScroll={{
          hasNextPage,
          isFetchingNextPage: listQuery.isFetchingNextPage,
          onLoadMore: () => void listQuery.fetchNextPage(),
        }}
        toolbar={{
          search: {
            value: searchInput,
            onChange: setSearchInput,
            onSubmit: () => {
              setSearch(searchInput.trim());
            },
            placeholder: "Search name or email…",
          },
        }}
        onRowClick={setSelected}
        activeKey={selected?.id}
        empty={{ icon: UsersIcon, title: "No users found", hint: search ? "Try a different search." : undefined }}
      />

      <RightSidebarPanel
        open={!!selected}
        title={selected?.displayName ?? "User"}
        subtitle={selected?.email}
        onClose={() => {
          setSelected(null);
          setSidebarTab("account");
          if (searchParams.get("tab")) {
            searchParams.delete("tab");
            setSearchParams(searchParams, { replace: true });
          }
        }}
        footer={
          selected && sidebarTab === "account" && can("rbac.manage") ? (
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>
                Close
              </Button>
              <Button
                size="sm"
                disabled={rolesMut.isPending}
                onClick={() => selected && rolesMut.mutate({ userId: selected.id, roleIds })}
              >
                Save roles
              </Button>
            </div>
          ) : undefined
        }
      >
        {selected && (
          <div className="space-y-5">
            {(vaultAccess || flexxiAccess) && (
              <PillSegmentSwitcher
                options={[
                  { value: "account", label: "Account" },
                  ...(vaultAccess ? [{ value: "vault" as const, label: "Vault" }] : []),
                  ...(flexxiAccess ? [{ value: "flexxi" as const, label: "FlexxiPlan" }] : []),
                ]}
                value={sidebarTab}
                onChange={(tab) => {
                  setSidebarTab(tab as SidebarTab);
                  const next = new URLSearchParams(searchParams);
                  if (tab === "vault" || tab === "flexxi") next.set("tab", tab);
                  else next.delete("tab");
                  setSearchParams(next, { replace: true });
                }}
              />
            )}

            {sidebarTab === "flexxi" && flexxiAccess ? (
              flexxiLoading ? (
                <p className="text-center text-xs text-white/50">Loading FlexxiPlan…</p>
              ) : (
                <div className="space-y-3 text-sm">
                  {flexxiData?.profile && (
                    <p className="text-white/60">
                      Reputation {flexxiData.profile.reputationScore} · Slots {flexxiData.profile.activeSlotLimit} ·
                      Completed {flexxiData.profile.completedPlans}
                    </p>
                  )}
                  {!flexxiData?.plans.length ? (
                    <EmptyState icon={Archive} title="No FlexxiPlan contracts" />
                  ) : (
                    flexxiData.plans.map((p) => (
                      <div key={p.id} className="rounded-xl border border-line px-3 py-2">
                        <div className="font-medium">{p.template.code}</div>
                        <div className="text-xs text-white/50">
                          {p.status} · {p.paidTotal} / {p.totalPayable}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )
            ) : sidebarTab === "vault" && vaultAccess ? (
              <>
                <div className="flex flex-wrap gap-2">
                  <Select
                    label="Status"
                    value={vaultStatus}
                    onChange={(e) => setVaultStatus(e.target.value)}
                    className="min-w-[140px]"
                  >
                    <option value="">All statuses</option>
                    <option value="IN_VAULT">In vault</option>
                    <option value="SHIPMENT_REQUESTED">Shipment requested</option>
                    <option value="SHIPPED">Shipped</option>
                  </Select>
                  <Select
                    label="Source"
                    value={vaultSource}
                    onChange={(e) => setVaultSource(e.target.value)}
                    className="min-w-[140px]"
                  >
                    <option value="">All sources</option>
                    <option value="PACK_PULL">Pack pull</option>
                    <option value="RAFFLE_WIN">Raffle win</option>
                    <option value="AUCTION_WIN">Auction win</option>
                    <option value="TRADE">Trade</option>
                  </Select>
                </div>

                {vaultData && (
                  <div className="flex gap-4 rounded-xl border border-line bg-white/[0.02] px-3 py-2 text-xs text-white/60">
                    <span>
                      <span className="text-white/40">Items </span>
                      <span className="font-medium tabular-nums text-white">{vaultData.summary.total}</span>
                    </span>
                    <span>
                      <span className="text-white/40">Est. value </span>
                      <span className="font-medium tabular-nums text-brand-pink">{rm(vaultData.summary.totalEstValue)}</span>
                    </span>
                  </div>
                )}

                {vaultLoading ? (
                  <p className="text-center text-xs text-white/50">Loading vault…</p>
                ) : !vaultData?.items.length ? (
                  <EmptyState icon={Archive} title="Vault is empty" hint="No items match the current filters." />
                ) : (
                  <div className="sidebar-card-grid">
                    {vaultData.items.map((item) => (
                      <div
                        key={item.id}
                        className="overflow-hidden rounded-2xl border border-line bg-white/[0.02] text-left"
                      >
                        {item.image ? (
                          <img src={item.image} alt="" className="aspect-square w-full object-cover" loading="lazy" />
                        ) : (
                          <div className="flex aspect-square items-center justify-center bg-white/5 text-white/30">
                            <Archive size={28} strokeWidth={1.25} />
                          </div>
                        )}
                        <div className="p-2.5">
                          <p className="line-clamp-2 text-[11px] font-medium leading-snug">{item.name}</p>
                          <div className="mt-1 flex items-center justify-between gap-1">
                            <span className="text-[10px] text-white/45">{rm(item.value)}</span>
                            <Badge color={statusColor(item.status)}>{item.status.replace(/_/g, " ")}</Badge>
                          </div>
                          <p className="mt-1 text-[10px] text-white/35">{item.sourceType.replace(/_/g, " ")}</p>
                          <p className="text-[10px] text-white/30">{fmtDateDMY(item.acquiredAt)}</p>
                          {item.shipmentOrderId && (
                            <p className="mt-1 truncate text-[10px] text-white/40">Order {item.shipmentOrderId.slice(0, 8)}…</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
            <div className="flex items-center justify-between rounded-xl border border-line bg-white/[0.02] px-3 py-2">
              <div>
                <p className="text-[10px] uppercase text-white/40">Wallet balance</p>
                <p className="font-display text-lg font-bold tabular-nums">{num(selected.walletBalance)} credits</p>
              </div>
              <Badge color={statusColor(selected.status)}>{selected.status}</Badge>
            </div>

            <div className="rounded-xl border border-line bg-white/[0.02] px-3 py-2">
              <p className="text-[10px] uppercase text-white/40">Mobile</p>
              <p className="mt-0.5 text-sm">{formatMobile(selected.phone, selected.phoneVerifiedAt)}</p>
            </div>

            {can("rbac.manage") && (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-white/40">Roles</p>
                <div className="space-y-1.5">
                  {(rolesData?.roles ?? []).map((role) => (
                    <label
                      key={role.id}
                      className="flex cursor-pointer items-center gap-2 rounded-lg border border-line/60 px-3 py-2 text-sm hover:bg-white/[0.03]"
                    >
                      <input
                        type="checkbox"
                        checked={roleIds.includes(role.id)}
                        onChange={() => toggleRole(role.id)}
                        className="accent-brand-pink"
                      />
                      <span>{role.label}</span>
                      <span className="ml-auto text-[10px] text-white/30">{role.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {can("users.suspend") && (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-white/40">Account</p>
                <Button
                  variant={selected.status === "SUSPENDED" ? "outline" : "danger"}
                  size="sm"
                  disabled={suspendMut.isPending}
                  onClick={() =>
                    confirm({
                      title: selected.status === "SUSPENDED" ? "Reactivate user?" : "Suspend user?",
                      message:
                        selected.status === "SUSPENDED"
                          ? "They will be able to sign in again."
                          : "They will be signed out and blocked from login.",
                      confirmLabel: selected.status === "SUSPENDED" ? "Reactivate" : "Suspend",
                      danger: selected.status !== "SUSPENDED",
                      action: () =>
                        suspendMut.mutateAsync({
                          userId: selected.id,
                          suspend: selected.status !== "SUSPENDED",
                        }),
                    })
                  }
                >
                  {selected.status === "SUSPENDED" ? "Reactivate" : "Suspend"}
                </Button>
              </div>
            )}

            {can("users.adjust_credit") && (
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-white/40">Adjust credits</p>
                <Input
                  label="Delta (+/-)"
                  type="number"
                  step="0.01"
                  value={creditDelta}
                  onChange={(e) => setCreditDelta(e.target.value)}
                  hint="Positive adds credits, negative deducts"
                />
                <Textarea
                  label="Note"
                  rows={2}
                  value={creditNote}
                  onChange={(e) => setCreditNote(e.target.value)}
                  placeholder="Reason for adjustment (min 3 chars)"
                />
                <Button
                  size="sm"
                  disabled={creditMut.isPending || !creditDelta || creditNote.trim().length < 3}
                  onClick={() =>
                    creditMut.mutate({
                      userId: selected.id,
                      delta: Number(creditDelta),
                      note: creditNote.trim(),
                    })
                  }
                >
                  Apply adjustment
                </Button>
              </div>
            )}
              </>
            )}
          </div>
        )}
      </RightSidebarPanel>
    </ListPageShell>
  );
}
